import React, { useEffect } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { ShimmerThumbnail } from "react-shimmer-effects"
import {
  Badge,
  BreadcrumbItem,
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Table,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import isPermissionEnabled from "common/isPermissionEnabled"
import formatDateTime from "components/Shared/formatDateTime"
import { getBranchRatingDetails } from "store/actions/ratingsAction"
import StarRatings from "react-star-ratings"
import { useTranslation } from "react-i18next"

const BranchRatingDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Branch Rating Details ")
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const { loading, isLoading, branchRatingDetails } = useSelector(
    state => state.Ratings
  )

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      27,
      "rating_1",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
  }, [userRole])

  useEffect(() => {
    getOldValues()
  }, [])

  const getOldValues = () => {
    dispatch(getBranchRatingDetails(id))
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        {loading ? (
          <Card>
            <CardBody>
              <Row>
                <Col lg={12}>
                  <ShimmerThumbnail height={390} rounded />
                </Col>
              </Row>
            </CardBody>
          </Card>
        ) : (
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-3 d-flex align-items-center">
                    <BackButton /> {t("Branch Rating Details")}
                  </CardTitle>
                  <div>
                    {Object.values(branchRatingDetails).length === 0 ? (
                      <p className="text-center my-2">
                        {t("No question details found")}
                      </p>
                    ) : (
                      <Table className="table-nowrap mb-0">
                        <tbody>
                          <tr>
                            <th scope="row">{t("Id")}</th>
                            <td>{branchRatingDetails._id}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Branch Name")}</th>
                            <td>
                              {language === "sa"
                                ? branchRatingDetails?.branchId?.nameAr
                                : branchRatingDetails?.branchId?.name || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Customer Name")}</th>
                            <td>
                              {branchRatingDetails?.userInfo?.name || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Customer Email")}</th>
                            <td>
                              {branchRatingDetails?.userInfo?.email || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Phone Number")}</th>
                            <td>
                              {branchRatingDetails?.userInfo?.phoneCode ||
                              branchRatingDetails?.userInfo?.phoneNumber ? (
                                <span
                                  dir="ltr"
                                  style={{ unicodeBidi: "bidi-override" }}
                                >
                                  {`${
                                    branchRatingDetails?.userInfo?.phoneCode ||
                                    ""
                                  } ${
                                    branchRatingDetails?.userInfo
                                      ?.phoneNumber || ""
                                  }`.trim()}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Customer DOB")}</th>
                            <td>{branchRatingDetails?.userInfo?.dob || "-"}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Platform")}</th>
                            <td>{t(branchRatingDetails?.platform) || "-"}</td>
                          </tr>

                          <tr>
                            <th scope="row">{t("Feedback")}</th>
                            <td>{branchRatingDetails?.feedback || "-"}</td>
                          </tr>

                          <tr>
                            <th scope="row">{t("Created At")}</th>
                            <td>
                              {formatDateTime(branchRatingDetails?.createdAt)}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Updated At")}</th>
                            <td>
                              {formatDateTime(branchRatingDetails?.updatedAt)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
        {loading ? (
          <Card>
            <CardBody>
              <Row>
                <Col lg={12}>
                  <ShimmerThumbnail height={390} rounded />
                </Col>
              </Row>
            </CardBody>
          </Card>
        ) : (
          <Row>
            <Col>
              <Card>
                <CardBody>
                  <CardTitle>{t("Ratings")} </CardTitle>
                  <div className="table-responsive">
                    {branchRatingDetails?.ratings?.length === 0 ? (
                      <p className="text-center my-3">{t("No rating found")}</p>
                    ) : (
                      <Table className="table mb-0" hover>
                        <thead className="table-light">
                          <tr>
                            <th>{t("Question")}</th>
                            {/* <th>Type</th>
                            <th>Scale</th> */}
                            <th>{t("Rating")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {branchRatingDetails?.ratings?.map(item => (
                            <tr key={item._id}>
                              <td>
                                {language === "sa"
                                  ? item?.questionId?.question?.ar
                                  : item?.questionId?.question?.en}
                              </td>
                              {/* <td>
                                {item?.questionId?.type === 0 && "Star"}
                                {item?.questionId?.type === 1 && "Scale"}
                              </td> */}
                              {/* <td>{item?.questionId?.scale}</td> */}
                              <td>
                                {/* <span className="badge bg-success font-size-12">
                                  <i className="mdi mdi-star me-1"></i>{" "}
                                  {item?.rating}
                                </span> */}
                                {item?.questionId?.type === 0 && (
                                  <StarRatings
                                    rating={item?.rating}
                                    starRatedColor="#F1B44C"
                                    starEmptyColor="#74788d"
                                    numberOfStars={item?.questionId?.scale}
                                    name="rating"
                                    starDimension="14px"
                                    starSpacing="3px"
                                  />
                                )}
                                {item?.questionId?.type === 1 &&
                                  `(0-${item?.questionId?.scale}) - ${item?.rating} `}{" "}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  )
}

export default BranchRatingDetails
